/**
 * Lightweight in-app analytics tracker.
 *
 *   import { track, pageView } from "@/lib/analytics";
 *   track("form_open", { form: "registration" });
 *
 * Events are batched in memory and flushed every 4 seconds (or on
 * `beforeunload` via `sendBeacon`). No cookies — visitor id lives in
 * localStorage, session id in sessionStorage.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VISITOR_KEY = "phc-visitor-id";
const SESSION_KEY = "phc-session-id";
const UTM_KEY = "phc-utm";

function uid() {
  return (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) + Date.now().toString(36);
}

function visitorId() {
  if (typeof window === "undefined") return "server";
  let v = window.localStorage.getItem(VISITOR_KEY);
  if (!v) {
    v = "v-" + uid();
    window.localStorage.setItem(VISITOR_KEY, v);
  }
  return v;
}

function sessionId() {
  if (typeof window === "undefined") return "server";
  let s = window.sessionStorage.getItem(SESSION_KEY);
  if (!s) {
    s = "s-" + uid();
    window.sessionStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

/** Read UTM tags from the URL once per session, cache them. */
function utmParams() {
  if (typeof window === "undefined") return {};
  const stored = window.sessionStorage.getItem(UTM_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      /* fall through */
    }
  }
  const params = new URLSearchParams(window.location.search);
  const out = {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
  window.sessionStorage.setItem(UTM_KEY, JSON.stringify(out));
  return out;
}

let queue = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, 4000);
}

async function flush() {
  if (queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      // Beacon uses a blob POST — most reliable on page unload
      const blob = new Blob([JSON.stringify({ events })], { type: "application/json" });
      const ok = navigator.sendBeacon(`${API}/analytics/events`, blob);
      if (ok) return;
    }
    await axios.post(`${API}/analytics/events`, { events }, { timeout: 4000 });
  } catch {
    /* silent — never let analytics break the site */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", flush);
  window.addEventListener("pagehide", flush);
  // Also flush every 15s regardless (long sessions)
  setInterval(flush, 15000);
}

export function track(kind, metadata = {}) {
  if (typeof window === "undefined") return;
  const utm = utmParams();
  queue.push({
    kind,
    path: window.location.pathname + window.location.search,
    visitor_id: visitorId(),
    session_id: sessionId(),
    referrer: document.referrer || "",
    utm_source: utm.utm_source || "",
    utm_medium: utm.utm_medium || "",
    utm_campaign: utm.utm_campaign || "",
    metadata: metadata || {},
    ts: new Date().toISOString(),
  });
  // Flush chat / form_submit / cta_click immediately-ish
  if (kind === "form_submit" || kind === "chat_lead_captured" || kind === "download_pass") {
    flush();
  } else {
    scheduleFlush();
  }
  // Mirror conversion events into GTM's dataLayer so Google Ads / Meta
  // conversion tags can trigger without any component-level code changes.
  // GTM initialises `window.dataLayer` in index.html; we defensively fall
  // back to an array if a page loaded before GTM's snippet ran.
  pushConversionToDataLayer(kind, metadata);
}

// ---------- GTM dataLayer bridge ---------------------------------------
//
// Kept intentionally in the same module as `track()` so every conversion
// event fired anywhere in the app auto-forwards to GTM. If you add a new
// conversion event kind in future, just add a mapping row below — no
// changes needed elsewhere.
//
// Naming convention: `phc_<action>`. All events are prefixed so client-
// side conversions never collide with GTM's built-in events (page_view,
// gtm.js, gtm.dom, etc). The generic GA4 event name is also fired for
// convenience (e.g. `generate_lead` on any form submit).

// SHA-256 hex hash via Web Crypto (browser-native, no dep).
// Google & Meta both require lowercased-trimmed input, SHA-256 hex output,
// for Enhanced Conversions / Advanced Matching. Returns "" if crypto is
// unavailable (very old browsers) so a missing hash never breaks the site.
async function sha256Hex(input) {
  try {
    if (!input || typeof input !== "string") return "";
    const normalised = input.trim().toLowerCase();
    if (!normalised) return "";
    const enc = new TextEncoder().encode(normalised);
    const buf = await window.crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

// Normalise a phone to E.164 (+countrycode + digits). Falls back to a
// bare-digits form so the hash is still deterministic even when the user
// typed spaces / dashes / brackets.
function normalisePhone(raw) {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  // Keep leading + then only digits.
  const digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  // Indian mobiles default to +91 if 10 digits given
  if (/^\d{10}$/.test(digits)) return "+91" + digits;
  return digits.startsWith("0") ? "+91" + digits.slice(1) : digits;
}

/**
 * Push an Enhanced Conversions payload to the dataLayer for Google Ads
 * (user_data.sha256_email_address / sha256_phone_number) AND Meta
 * Advanced Matching (em / ph / fn as SHA-256). Both platforms accept
 * hashed identifiers so no raw PII ever leaves the client.
 *
 * Runs async because Web Crypto returns Promises; the caller doesn't
 * need to await — it's fire-and-forget.
 */
async function pushEnhancedConversion({ email, phone, firstName, form, source }) {
  try {
    const [em, ph, fn] = await Promise.all([
      sha256Hex(email || ""),
      sha256Hex(normalisePhone(phone || "")),
      sha256Hex((firstName || "").split(/\s+/)[0] || ""),
    ]);
    if (!em && !ph) return; // nothing to enhance with — skip
    if (!window.dataLayer) window.dataLayer = [];
    window.dataLayer.push({
      event: "phc_enhanced_conversion",
      form: form || "",
      source: source || "",
      // Google Ads Enhanced Conversions user_data shape
      enhanced_conversion_data: {
        sha256_email_address: em,
        sha256_phone_number: ph,
      },
      // Duplicate flat keys for GTM DLV convenience & Meta Advanced Matching
      hashed_email: em,
      hashed_phone: ph,
      hashed_first_name: fn,
    });

    // Meta Advanced Matching — fire the `Lead` conversion with hashed
    // identifiers directly against the already-loaded Meta Pixel. Meta's
    // fbq accepts hashed values in the 4th (user_data) argument and
    // recognises them by shape — no need to declare them as pre-hashed.
    // Runs only if the Pixel snippet in index.html has finished loading.
    if (typeof window.fbq === "function") {
      window.fbq(
        "track",
        "Lead",
        { form: form || "", source: source || "" },
        { em, ph, fn },
      );
    }
  } catch {
    /* never let PII hashing break the site */
  }
}

const CONVERSION_MAP = {
  form_submit: (m = {}) => [
    // GA4 recommended event for lead capture
    { event: "generate_lead", form: m.form || "", source: m.source || "", currency: "INR", value: 0 },
    // Form-specific, easier GTM trigger authoring
    { event: `phc_form_submit_${m.form || "unknown"}`, source: m.source || "" },
    // Umbrella event
    { event: "phc_form_submit", form: m.form || "", source: m.source || "" },
  ],
  chat_lead_captured: (m = {}) => [
    { event: "generate_lead", form: "chatbot", source: "chatbot", currency: "INR", value: 0 },
    { event: "phc_chat_lead", has_name: !!m.name, has_email: !!m.email },
  ],
  download_pass: (m = {}) => [
    { event: "phc_download_pass", ticket: m.ticket || "" },
  ],
  share_whatsapp: (m = {}) => [
    { event: "phc_whatsapp_click", ticket: m.ticket || "", source: m.source || "" },
  ],
};

function pushConversionToDataLayer(kind, metadata) {
  try {
    const builder = CONVERSION_MAP[kind];
    if (!builder) return;
    if (!window.dataLayer) window.dataLayer = [];
    for (const payload of builder(metadata || {})) {
      window.dataLayer.push(payload);
    }
    // For lead-capture events, additionally push the Enhanced Conversion
    // payload (hashed email/phone) so Google Ads Enhanced Conversions and
    // Meta Advanced Matching can attribute the conversion. Runs async and
    // never blocks the primary push.
    const isLead = kind === "form_submit" || kind === "chat_lead_captured";
    if (isLead && (metadata?.email || metadata?.phone || metadata?.mobile)) {
      pushEnhancedConversion({
        email: metadata.email,
        phone: metadata.phone || metadata.mobile,
        firstName: metadata.name,
        form: metadata.form || (kind === "chat_lead_captured" ? "chatbot" : ""),
        source: metadata.source || "",
      });
    }
  } catch {
    /* never let analytics break the site */
  }
}

export function pageView(path) {
  track("page_view", { path: path || (typeof window !== "undefined" ? window.location.pathname : "/") });
}
